import type { CustomExperimentSpec, PanelRow } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createChartCard } from '../render/chart-card';
import { createVenn, type VennConfig, type VennMember } from '../render/venn';
import {
  createTree,
  leafProbabilities,
  leafPaths,
  leafBranchProbabilities,
  type TreeNode,
} from '../render/tree';
import {
  createDataTable,
  type DataTableColumn,
  type DataTableHandle,
  type DataTableStat,
} from '@learnforge/demo-kit';
import type { I18n } from '@learnforge/i18n';
import { MASTER_SURVEY } from '../data/class-survey';
import {
  VENN_PRESETS,
  TREE_PRESETS,
  type VennPresetId,
  type TreePresetId,
} from '../data/presets/e5-events';
import {
  isSumCondition,
  buildCoinTree,
  buildPerDieTree,
  buildSumTree,
  buildCustomTree,
  resizeEvents,
  conditionFraction,
  type DiceCondition,
  type PerDieCondition,
  type SumCondition,
} from '../statistics/event-trees';
import type { StatsContext, DemoKey } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Mode = 'venn' | 'tree';

interface State {
  mode: Mode;
  vennPresetId: VennPresetId;
  treePresetId: TreePresetId;
  universeSize: number;
  tosses: number;
  dice: number;
  condition: DiceCondition;
  /** Custom preset: outcome count (2..5) of the repeated event. */
  customEvents: number;
  /** Custom preset: how many times the event repeats (occurrences/levels). */
  customLevels: number;
}

const PER_DIE_CONDITIONS: readonly PerDieCondition[] = ['even', 'odd', 'lt4', 'ge5'];
const SUM_CONDITIONS: readonly SumCondition[] = ['sum-lt8', 'sum-eq7', 'doubles'];

/**
 * The `pd-condition` dropdown's option list for a given `dice` count — sum
 * conditions (`sum-lt8`/`sum-eq7`/`doubles`) only make sense for exactly two
 * dice. Shared by `panel()` (full render) and `updateConditionOptions` in
 * `createRender` (the dice-slider's in-place DOM patch, see below) so the two
 * option sets cannot drift apart.
 */
const CONDITION_LABEL_KEYS: Record<DiceCondition, DemoKey> = {
  even: 'cond.even',
  odd: 'cond.odd',
  lt4: 'cond.lt4',
  ge5: 'cond.ge5',
  'sum-lt8': 'cond.sum-lt8',
  'sum-eq7': 'cond.sum-eq7',
  doubles: 'cond.doubles',
};

function diceConditionOptions(dice: number): { value: DiceCondition; labelKey: DemoKey }[] {
  return [
    ...PER_DIE_CONDITIONS.map((c) => ({ value: c, labelKey: CONDITION_LABEL_KEYS[c] })),
    ...(dice === 2
      ? SUM_CONDITIONS.map((c) => ({ value: c, labelKey: CONDITION_LABEL_KEYS[c] }))
      : []),
  ];
}

/** The custom tree's `pd-events` dropdown options — 2..5 outcomes. */
const CUSTOM_EVENTS_OPTIONS: { value: string; labelKey: DemoKey }[] = [
  { value: '2', labelKey: 'count.2' },
  { value: '3', labelKey: 'count.3' },
  { value: '4', labelKey: 'count.4' },
  { value: '5', labelKey: 'count.5' },
];

/** Widest custom tree kept to ≤ 32 leaves/probability lines. */
const MAX_CUSTOM_LEAVES = 32;

/** Largest level count (1..4) that keeps `events ** levels <= MAX_CUSTOM_LEAVES`. */
function maxCustomLevels(events: number): number {
  let max = 1;
  for (let l = 1; l <= 4; l++) {
    if (events ** l <= MAX_CUSTOM_LEAVES) max = l;
  }
  return max;
}

// ---------------------------------------------------------------------------
// Venn member sources — build the member list per preset
// ---------------------------------------------------------------------------

/** Members for the class-sports presets: every `MASTER_SURVEY` student,
 * labeled by name, with `sets` derived from `sports_played` membership. */
function surveyMembers(sportIds: readonly string[]): VennMember[] {
  const setIds = ['A', 'B', 'C'] as const;
  return MASTER_SURVEY.rows.map((row) => ({
    id: row.name,
    label: row.name,
    sets: sportIds.flatMap((sport, i) => (row.sports_played.includes(sport) ? [setIds[i]!] : [])),
  }));
}

function buildVennConfig(presetId: VennPresetId, universeSize: number): VennConfig<DemoKey> {
  if (presetId === 'dice-even-or-prime') {
    const EVEN = new Set([2, 4, 6]);
    const PRIME = new Set([2, 3, 5]);
    const members: VennMember[] = [1, 2, 3, 4, 5, 6].map((face) => ({
      id: String(face),
      label: String(face),
      sets: [
        ...(EVEN.has(face) ? (['A'] as const) : []),
        ...(PRIME.has(face) ? (['B'] as const) : []),
      ],
    }));
    return {
      sets: [
        { id: 'A', labelKey: 'venn.set.even', cx: 180, cy: 170, r: 90 },
        { id: 'B', labelKey: 'venn.set.prime', cx: 300, cy: 170, r: 90 },
      ],
      members,
    };
  }

  if (presetId === 'class-sports-2set') {
    return {
      sets: [
        { id: 'A', labelKey: 'tag.football.label', cx: 180, cy: 170, r: 90 },
        { id: 'B', labelKey: 'tag.basketball.label', cx: 300, cy: 170, r: 90 },
      ],
      members: surveyMembers(['football', 'basketball']),
    };
  }

  if (presetId === 'class-sports-3set') {
    return {
      sets: [
        { id: 'A', labelKey: 'tag.football.label', cx: 200, cy: 130, r: 90 },
        { id: 'B', labelKey: 'tag.basketball.label', cx: 280, cy: 130, r: 90 },
        { id: 'C', labelKey: 'tag.volleyball.label', cx: 240, cy: 210, r: 90 },
      ],
      members: surveyMembers(['football', 'basketball', 'volleyball']),
    };
  }

  // custom — deterministic universe split using the historical region-count
  // formulas: the first `ab` synthesized members go to A∩B, the next `aOnly`
  // to A only, the next `bOnly` to B only, the rest to neither.
  const ab = Math.floor(universeSize / 6);
  const aOnly = Math.floor(universeSize / 2) - ab;
  const bOnly = Math.floor(universeSize / 3) - ab;
  const none = universeSize - aOnly - bOnly - ab;
  const members: VennMember[] = [];
  const pushMembers = (count: number, sets: readonly ('A' | 'B')[]) => {
    for (let i = 0; i < Math.max(0, count); i++) {
      const label = `#${members.length + 1}`;
      members.push({ id: label, label, sets });
    }
  };
  pushMembers(ab, ['A', 'B']);
  pushMembers(aOnly, ['A']);
  pushMembers(bOnly, ['B']);
  pushMembers(none, []);

  return {
    sets: [
      { id: 'A', labelKey: 'region.A', cx: 180, cy: 170, r: 90 },
      { id: 'B', labelKey: 'region.B', cx: 300, cy: 170, r: 90 },
    ],
    members,
  };
}

// ---------------------------------------------------------------------------
// Probability helpers — computed FROM the member list (count membership)
// ---------------------------------------------------------------------------

interface TwoSetProbs {
  n: number;
  a: number;
  b: number;
  ab: number;
  pA: number;
  pB: number;
  pAB: number;
  pAuB: number;
  pAgivenB: number;
  pBgivenA: number;
}

function countInSet(members: readonly VennMember[], id: 'A' | 'B' | 'C'): number {
  return members.filter((m) => m.sets.includes(id)).length;
}

function computeTwoSetProbs(members: readonly VennMember[]): TwoSetProbs {
  const n = members.length;
  const a = countInSet(members, 'A');
  const b = countInSet(members, 'B');
  const ab = members.filter((m) => m.sets.includes('A') && m.sets.includes('B')).length;
  const pA = n ? a / n : NaN;
  const pB = n ? b / n : NaN;
  const pAB = n ? ab / n : NaN;
  const pAuB = pA + pB - pAB;
  const pAgivenB = pB > 0 ? pAB / pB : NaN;
  const pBgivenA = pA > 0 ? pAB / pA : NaN;
  return { n, a, b, ab, pA, pB, pAB, pAuB, pAgivenB, pBgivenA };
}

interface ProbRow {
  label: string;
  value: number;
  /** The formula + substitution shown under the row (e.g. `|Even| / n =
   * 3/6`) — set names and counts, never i18n keys (symbols are
   * language-neutral). Ignored by the data table's Σ popover, which only
   * ever shows label/value. */
  detail: string;
}

/** `x/y` formatted for a detail line — plain integers, no probability
 * rounding. */
function ratio(x: number, y: number): string {
  return `${x}/${y}`;
}

/** Builds the probability rows shown in both the side probs list and the
 * data table's stats popup, from the sets' TRANSLATED names — the single
 * source of truth so the two views can't drift. */
function buildProbRows(config: VennConfig<DemoKey>, t: (key: DemoKey) => string): ProbRow[] {
  const setA = config.sets.find((s) => s.id === 'A')!;
  const setB = config.sets.find((s) => s.id === 'B')!;
  const nameA = t(setA.labelKey);
  const nameB = t(setB.labelKey);
  const probs = computeTwoSetProbs(config.members);
  const { n, a, b, ab, pA, pB, pAB, pAuB, pAgivenB, pBgivenA } = probs;

  const rows: ProbRow[] = [
    { label: `P(${nameA})`, value: pA, detail: `|${nameA}| / n = ${ratio(a, n)}` },
    { label: `P(${nameB})`, value: pB, detail: `|${nameB}| / n = ${ratio(b, n)}` },
  ];

  if (config.sets.length === 3) {
    const setC = config.sets.find((s) => s.id === 'C')!;
    const nameC = t(setC.labelKey);
    const c = countInSet(config.members, 'C');
    rows.push({
      label: `P(${nameC})`,
      value: n ? c / n : NaN,
      detail: `|${nameC}| / n = ${ratio(c, n)}`,
    });
  }

  rows.push(
    {
      label: `P(${nameA} ∩ ${nameB})`,
      value: pAB,
      detail: `|${nameA} ∩ ${nameB}| / n = ${ratio(ab, n)}`,
    },
    {
      label: `P(${nameA} ∪ ${nameB})`,
      value: pAuB,
      detail: `P(${nameA}) + P(${nameB}) − P(${nameA} ∩ ${nameB}) = ${pA.toFixed(3)} + ${pB.toFixed(3)} − ${pAB.toFixed(3)}`,
    },
    {
      label: `P(${nameA} | ${nameB})`,
      value: pAgivenB,
      detail: Number.isNaN(pAgivenB)
        ? `P(${nameA} ∩ ${nameB}) / P(${nameB})`
        : `P(${nameA} ∩ ${nameB}) / P(${nameB}) = ${pAB.toFixed(3)} / ${pB.toFixed(3)}`,
    },
    {
      label: `P(${nameB} | ${nameA})`,
      value: pBgivenA,
      detail: Number.isNaN(pBgivenA)
        ? `P(${nameB} ∩ ${nameA}) / P(${nameA})`
        : `P(${nameB} ∩ ${nameA}) / P(${nameA}) = ${pAB.toFixed(3)} / ${pA.toFixed(3)}`,
    },
  );

  return rows;
}

function formatProb(value: number): string {
  return Number.isNaN(value) ? '—' : value.toFixed(3);
}

// ---------------------------------------------------------------------------
// Side panel probability list
// ---------------------------------------------------------------------------

/** Renders `rows` as one line per row: label, derivation, and value chained
 * with '=' (P(Even) = |Even| / n = 3/6 = 0.500). The label and final result
 * are emphasized (primary ink); the middle derivation stays muted. A row
 * with no `detail` renders `label = result` — skipping the middle segment
 * entirely so there's no doubled '='. Shared by venn's `renderProbsList` and
 * tree's `renderTreeProbsList`; venn's output is pinned by tests, so this
 * must stay byte-identical to what it replaced. */
function renderProbLines(rows: ProbRow[]): HTMLElement {
  const list = document.createElement('div');
  list.className = 'pd-prob-lines';

  for (const row of rows) {
    const line = document.createElement('div');
    line.className = 'pd-prob-line';
    const label = document.createElement('span');
    label.className = 'pd-prob-strong';
    label.textContent = row.label;
    const result = document.createElement('span');
    result.className = 'pd-prob-strong';
    result.textContent = formatProb(row.value);
    if (row.detail) {
      line.append(label, ` = ${row.detail} = `, result);
    } else {
      line.append(label, ` = `, result);
    }
    list.appendChild(line);
  }

  return list;
}

function renderProbsList(config: VennConfig<DemoKey>, t: (key: DemoKey) => string): HTMLElement {
  return renderProbLines(buildProbRows(config, t));
}

// ---------------------------------------------------------------------------
// Venn data table (members + membership columns)
// ---------------------------------------------------------------------------

function vennMemberRows(members: readonly VennMember[]): Record<string, unknown>[] {
  return members.map((m) => {
    const row: Record<string, unknown> = { element: m.label };
    for (const id of ['A', 'B', 'C'] as const) row[id] = m.sets.includes(id);
    return row;
  });
}

function vennTableStats(config: VennConfig<DemoKey>, t: (key: DemoKey) => string): DataTableStat[] {
  return [
    { label: t('stat.n'), value: String(config.members.length) },
    ...buildProbRows(config, t).map((row) => ({ label: row.label, value: formatProb(row.value) })),
  ];
}

function buildVennDataTable(
  getConfig: () => VennConfig<DemoKey>,
  i18n: I18n<DemoKey>,
  onToggle?: (rowIndex: number, setId: 'A' | 'B' | 'C', checked: boolean) => void,
): DataTableHandle {
  const columns: DataTableColumn<DemoKey>[] = [
    { id: 'element', labelKey: 'table.element' },
    ...getConfig().sets.map((s) => ({
      id: s.id,
      labelKey: s.labelKey,
      format: (v: unknown) => (v ? '✓' : ''),
      editable: Boolean(onToggle),
    })),
  ];
  return createDataTable<DemoKey>({
    i18n,
    columns,
    getRows: () => vennMemberRows(getConfig().members),
    getStats: () => vennTableStats(getConfig(), (k) => i18n.t(k)),
    rowNumbers: true,
    ...(onToggle
      ? {
          onToggle: (rowIndex: number, columnId: string, checked: boolean) => {
            if (columnId === 'A' || columnId === 'B' || columnId === 'C') {
              onToggle(rowIndex, columnId, checked);
            }
          },
        }
      : {}),
  });
}

/** Venn-consistent probabilities list for tree mode — same `renderProbLines`
 * shape as the venn view. Leaf rows: `P(path)` = branch probabilities ×'d
 * together = their product (only two-or-more-edge paths show a derivation —
 * a single-edge leaf's "derivation" would just repeat the result). The
 * dice-roll preset appends a condition row with an exact fraction derivation
 * from `conditionFraction`, so it can't drift from the tree's own odds. */
function renderTreeProbsList(
  state: State,
  root: TreeNode<DemoKey>,
  t: (key: DemoKey) => string,
): HTMLElement {
  const probs = leafProbabilities(root);
  const paths = leafPaths(root);
  const branchProbs = leafBranchProbabilities(root);

  const rows: ProbRow[] = probs.map((value, i) => {
    const eventKeys = (paths[i] ?? []).slice(1);
    const edges = branchProbs[i] ?? [];
    return {
      label: `P(${eventKeys.map(t).join('·')})`,
      value,
      detail:
        edges.length >= 2
          ? `${eventKeys.map((k) => `P(${t(k)})`).join(' × ')} = ${edges.map((p) => p.toFixed(3)).join(' × ')}`
          : '',
    };
  });

  if (state.treePresetId === 'dice-roll') {
    const { num, den } = conditionFraction(state.dice, state.condition);
    rows.push({
      label: `P(${t(CONDITION_LABEL_KEYS[state.condition])})`,
      value: num / den,
      detail: `${num}/${den}`,
    });
  }

  return renderProbLines(rows);
}

// ---------------------------------------------------------------------------
// Tree root resolution
// ---------------------------------------------------------------------------

function buildTree(state: State): TreeNode<DemoKey> {
  if (state.treePresetId === 'coin-toss') {
    return buildCoinTree(state.tosses);
  }
  if (state.treePresetId === 'dice-roll') {
    return isSumCondition(state.condition)
      ? buildSumTree(state.condition)
      : buildPerDieTree(state.dice, state.condition);
  }
  const preset = TREE_PRESETS.find((p) => p.id === state.treePresetId)!;
  // deep-clone so edits don't mutate the preset constant
  return JSON.parse(JSON.stringify(preset.root!)) as TreeNode<DemoKey>;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, DemoKey, StatsContext> {
  return {
    labelKey: 'exp.probability-diagrams.title',
    demoId: 'statistics',
    advancedKey: 'probability-diagrams',

    initialState(): State {
      return {
        mode: 'venn',
        vennPresetId: 'dice-even-or-prime',
        treePresetId: 'coin-toss',
        universeSize: 12,
        tosses: 2,
        dice: 2,
        condition: 'even',
        customEvents: 2,
        customLevels: 2,
      };
    },

    panel: (state): PanelRow<DemoKey, State>[] => {
      const rows: PanelRow<DemoKey, State>[] = [
        {
          kind: 'dropdown',
          id: 'pd-mode',
          labelKey: 'param.mode.label',
          options: [
            { value: 'venn', labelKey: 'mode.venn' },
            { value: 'tree', labelKey: 'mode.tree' },
          ],
          value: state.mode,
          onChange: (v) => (prev) => ({ ...prev, mode: v as Mode }),
        },
      ];

      if (state.mode === 'venn') {
        rows.push({
          kind: 'dropdown',
          id: 'pd-venn-preset',
          labelKey: 'param.preset.label',
          options: VENN_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
          value: state.vennPresetId,
          onChange: (v) => (prev) => ({ ...prev, vennPresetId: v as VennPresetId }),
        });

        if (state.vennPresetId === 'custom') {
          rows.push({
            kind: 'slider',
            id: 'pd-universe',
            labelKey: 'param.universe.label',
            min: 4,
            max: 200,
            step: 1,
            value: state.universeSize,
            format: (v) => String(v),
            onChange: (v) => (prev) => ({ ...prev, universeSize: v }),
          });
        }
      } else {
        rows.push({
          kind: 'dropdown',
          id: 'pd-tree-preset',
          labelKey: 'param.preset.label',
          options: TREE_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
          value: state.treePresetId,
          onChange: (v) => (prev) => ({ ...prev, treePresetId: v as TreePresetId }),
        });

        if (state.treePresetId === 'coin-toss') {
          rows.push({
            kind: 'slider',
            id: 'pd-tosses',
            labelKey: 'param.tosses.label',
            min: 1,
            max: 4,
            step: 1,
            value: state.tosses,
            format: (v) => String(v),
            onChange: (v) => (prev) => ({ ...prev, tosses: v }),
          });
        }

        if (state.treePresetId === 'dice-roll') {
          rows.push({
            kind: 'slider',
            id: 'pd-dice',
            labelKey: 'param.dice.label',
            min: 1,
            max: 4,
            step: 1,
            value: state.dice,
            format: (v) => String(v),
            // Updater form: the condition dropdown row (below) coexists with
            // this slider, and slider input skips the panel rebuild — so this
            // closure (and the dropdown's) can go stale relative to each other.
            onChange: (v) => (prev) => {
              const condition =
                v === 2 || !isSumCondition(prev.condition) ? prev.condition : 'even';
              return { ...prev, dice: v, condition };
            },
          });

          rows.push({
            kind: 'dropdown',
            id: 'pd-condition',
            labelKey: 'param.condition.label',
            options: diceConditionOptions(state.dice),
            value: state.condition,
            onChange: (v) => (prev) => ({ ...prev, condition: v as DiceCondition }),
          });
        }

        if (state.treePresetId === 'custom') {
          rows.push({
            kind: 'dropdown',
            id: 'pd-events',
            labelKey: 'param.events.label',
            options: CUSTOM_EVENTS_OPTIONS,
            value: String(state.customEvents),
            // A dropdown change rebuilds the panel — that's what lets the
            // levels slider's `max` recompute below (a slider's own input
            // never rebuilds the panel, so it can't clamp its own range).
            onChange: (v) => (prev) => {
              const events = Number(v);
              return {
                ...prev,
                customEvents: events,
                customLevels: Math.min(prev.customLevels, maxCustomLevels(events)),
              };
            },
          });

          rows.push({
            kind: 'slider',
            id: 'pd-levels',
            labelKey: 'param.levels.label',
            min: 1,
            max: maxCustomLevels(state.customEvents),
            step: 1,
            value: state.customLevels,
            format: (v) => String(v),
            onChange: (v) => (prev) => ({ ...prev, customLevels: v }),
          });
        }
      }

      return rows;
    },

    createRender(ctx, host) {
      // ── Root grid wrapper ────────────────────────────────────────────────────
      const grid = document.createElement('div');
      grid.className = 'stats-grid stats-grid--main-side';
      host.appendChild(grid);

      // ── Data table (venn mode only) ───────────────────────────────────────────
      // Built once up front — like two-variable-lab's table — purely so its
      // `.controls` (Σ / column-picker buttons) can be passed as `headerActions`
      // at card-construction time; `buildVenn` destroys + recreates it on every
      // rebuild (mode/preset/universe change), matching two-variable-lab's
      // destroy-on-dataset-switch pattern.
      let dataTable: DataTableHandle = buildVennDataTable(
        () => buildVennConfig('dice-even-or-prime', 12),
        ctx.i18n,
      );

      // ── Chart cards ──────────────────────────────────────────────────────────
      const tableCard = createChartCard<DemoKey>({
        titleKey: 'table.data',
        i18n: ctx.i18n,
        className: 'stats-grid-full',
        headerActions: [dataTable.controls],
        collapsible: { storageKey: 'learnforge:collapsible:statistics:probability-diagrams:table' },
      });
      const mainCard = createChartCard<DemoKey>({
        titleKey: 'card.venn',
        i18n: ctx.i18n,
        collapsible: { storageKey: 'learnforge:collapsible:statistics:probability-diagrams:main' },
      });
      const probsCard = createChartCard<DemoKey>({
        titleKey: 'card.probabilities',
        i18n: ctx.i18n,
        collapsible: {
          storageKey: 'learnforge:collapsible:statistics:probability-diagrams:probs',
        },
      });

      tableCard.setBody(dataTable.element);
      tableCard.element.hidden = true;
      grid.append(tableCard.element, mainCard.element, probsCard.element);

      // `headerActions` is constructor-only on createChartCard. Each venn
      // rebuild destroys + recreates the table (columns depend on the preset's
      // arity/labels) and swaps its `controls` into this div, mirroring
      // two-variable-lab's dataset-switch handling.
      const tableActions = tableCard.element.querySelector<HTMLElement>('.chart-card-actions')!;

      // ── Diagram handles (transient closure vars) ─────────────────────────────
      let vennHandle: ReturnType<typeof createVenn<DemoKey>> | null = null;
      let treeHandle: ReturnType<typeof createTree<DemoKey>> | null = null;
      // Custom-preset-only legend under the tree; tracked so `destroyDiagrams`
      // can remove it — `treeHandle.destroy()` only removes the svg, and
      // without this the legend would leak into venn mode after a mode switch.
      let treeLegend: HTMLElement | null = null;
      // Live venn config for the current mount — reassigned by `buildVenn` and
      // (immutably, per-toggle) by `toggleMembership`. `buildVennDataTable`'s
      // getters read this via closure so `dataTable.update()` sees post-toggle
      // state without a table rebuild.
      let vennConfig: VennConfig<DemoKey> = buildVennConfig('dice-even-or-prime', 12);
      // Custom tree's shared outcome-probability vector — one entry per
      // event, applied to every occurrence. `null` means "not yet
      // initialized for this preset visit"; `buildTreeDiagram` seeds it
      // uniform on first custom-preset build. Reset to `null` in `update()`
      // on mode/preset change (leaving/entering the preset resets edits);
      // NOT reset on events/levels changes, so redistribution sees the
      // user's current edited ratios.
      let customProbs: number[] | null = null;

      /** Toggles one member's membership in `setId`, then refreshes the venn
       * diagram, probs card, and data table in place. Immutable: builds a new
       * `members` array (and a new `vennConfig`) rather than mutating in
       * place — `surveyMembers()` already produced fresh member objects, so
       * this never touches `MASTER_SURVEY`. Session-local: switching preset
       * or moving the custom universe slider rebuilds `vennConfig` from
       * scratch via `buildVenn`, discarding any edits — that reset is
       * intended. */
      function toggleMembership(rowIndex: number, setId: 'A' | 'B' | 'C', checked: boolean): void {
        const members = vennConfig.members.map((m, i) =>
          i === rowIndex
            ? { ...m, sets: checked ? [...m.sets, setId] : m.sets.filter((s) => s !== setId) }
            : m,
        );
        vennConfig = { ...vennConfig, members };
        vennHandle?.update(vennConfig);
        probsCard.setBody(renderProbsList(vennConfig, (k) => ctx.i18n.t(k)));
        dataTable.update();
      }

      function destroyDiagrams(): void {
        vennHandle?.destroy();
        vennHandle = null;
        treeHandle?.destroy();
        treeHandle = null;
        treeLegend?.remove();
        treeLegend = null;
        mainCard.body.classList.remove('pd-tree-body');
        tableCard.element.hidden = true;
      }

      // ── Diagram creation helpers ─────────────────────────────────────────────

      function buildVenn(state: State): void {
        vennConfig = buildVennConfig(state.vennPresetId, state.universeSize);
        vennHandle = createVenn<DemoKey>({
          container: mainCard.body,
          i18n: ctx.i18n,
          initial: vennConfig,
        });
        probsCard.setBody(renderProbsList(vennConfig, (k) => ctx.i18n.t(k)));

        dataTable.destroy();
        // Dice preset stays read-only (a die face's evenness/primality is a
        // mathematical fact); class-sports and custom presets get live
        // membership checkboxes.
        dataTable = buildVennDataTable(
          () => vennConfig,
          ctx.i18n,
          state.vennPresetId !== 'dice-even-or-prime' ? toggleMembership : undefined,
        );
        tableCard.setBody(dataTable.element);
        tableActions.replaceChildren(dataTable.controls);
        tableCard.element.hidden = false;
      }

      function buildTreeDiagram(state: State): void {
        mainCard.body.classList.add('pd-tree-body');
        const isCustom = state.treePresetId === 'custom';

        if (isCustom) {
          // Reconcile the shared vector to the current event count BEFORE
          // building the tree: null seeds uniform, a length mismatch steps
          // via addEvent/removeEvent (resizeEvents) so edited ratios survive
          // an events change.
          customProbs =
            customProbs === null
              ? Array(state.customEvents).fill(1 / state.customEvents)
              : resizeEvents(customProbs, state.customEvents);
        }

        let rootNode: TreeNode<DemoKey> = isCustom
          ? buildCustomTree(customProbs!, state.customLevels)
          : buildTree(state);

        treeHandle = createTree<DemoKey>({
          container: mainCard.body,
          i18n: ctx.i18n,
          initial: rootNode,
          editable: isCustom,
          // `exactOptionalPropertyTypes` forbids `editScope: undefined` —
          // omit the key entirely for non-custom presets instead.
          ...(isCustom ? { editScope: 'tree' as const } : {}),
          onChange(updated) {
            rootNode = updated;
            if (isCustom) {
              // A tree-scoped edit rebalanced every occurrence to match, so
              // the root's children ARE the shared vector afterward.
              customProbs = (updated.children ?? []).map((c) => c.probability);
            }
            probsCard.setBody(renderTreeProbsList(state, rootNode, (k) => ctx.i18n.t(k)));
          },
        });
        probsCard.setBody(renderTreeProbsList(state, rootNode, (k) => ctx.i18n.t(k)));

        if (isCustom) {
          treeLegend = document.createElement('div');
          treeLegend.className = 'pd-tree-legend';
          treeLegend.textContent = ctx.i18n.t('tree.legend.custom');
          mainCard.body.appendChild(treeLegend);
        }
      }

      // ── In-place condition-dropdown patch (dice-slider boundary crossing) ────
      // A slider row's `input` events never rebuild the panel (see demo-render's
      // slider case in define-experiment.ts: rebuilding mid-drag would tear the
      // dragged <input type="range"> out of the DOM, ending the user's drag
      // gesture). So when `dice` crosses the ===2 boundary via the dice slider,
      // the condition dropdown's stale option list (sum options only valid at
      // dice===2) can't be refreshed by a normal render. Patch just that
      // <select>'s options in place instead of forcing a full panel rebuild.
      // No-ops safely when the select isn't mounted (mode=venn or a tree preset
      // other than dice-roll).
      function updateConditionOptions(state: State): void {
        const select = ctx.panel.querySelector<HTMLSelectElement>('#dropdown_pd-condition');
        if (!select) return;
        select.replaceChildren(
          ...diceConditionOptions(state.dice).map((o) => {
            const optEl = document.createElement('option');
            optEl.value = o.value;
            optEl.dataset.labelKey = o.labelKey;
            optEl.textContent = ctx.i18n.t(o.labelKey);
            return optEl;
          }),
        );
        select.value = state.condition;
      }

      // ── Tracked keys used to detect when to recreate the diagram ─────────────
      let lastMode: Mode | undefined;
      let lastVennPresetId: VennPresetId | undefined;
      let lastTreePresetId: TreePresetId | undefined;
      let lastUniverseSize: number | undefined;
      let lastTosses: number | undefined;
      let lastDice: number | undefined;
      let lastCondition: DiceCondition | undefined;
      let lastCustomEvents: number | undefined;
      let lastCustomLevels: number | undefined;

      // ── update (called after every state change) ─────────────────────────────
      function update(state: State): void {
        const modeChanged = state.mode !== lastMode;
        const vennPresetChanged = state.vennPresetId !== lastVennPresetId;
        const treePresetChanged = state.treePresetId !== lastTreePresetId;
        const universeSizeChanged = state.universeSize !== lastUniverseSize;
        const tossesChanged = state.tosses !== lastTosses;
        const diceChanged = state.dice !== lastDice;
        const conditionChanged = state.condition !== lastCondition;
        const customEventsChanged = state.customEvents !== lastCustomEvents;
        const customLevelsChanged = state.customLevels !== lastCustomLevels;
        const diceCrossedSumBoundary =
          lastDice !== undefined && (lastDice === 2) !== (state.dice === 2);

        // Leaving/entering the custom preset resets its edited probability
        // vector — existing convention for other tree presets' live state.
        if (modeChanged || treePresetChanged) {
          customProbs = null;
        }

        const needsRebuild =
          modeChanged ||
          (state.mode === 'venn' && (vennPresetChanged || universeSizeChanged)) ||
          (state.mode === 'tree' &&
            (treePresetChanged ||
              (state.treePresetId === 'coin-toss' && tossesChanged) ||
              (state.treePresetId === 'dice-roll' && (diceChanged || conditionChanged)) ||
              (state.treePresetId === 'custom' && (customEventsChanged || customLevelsChanged))));

        lastMode = state.mode;
        lastVennPresetId = state.vennPresetId;
        lastTreePresetId = state.treePresetId;
        lastUniverseSize = state.universeSize;
        lastTosses = state.tosses;
        lastDice = state.dice;
        lastCondition = state.condition;
        lastCustomEvents = state.customEvents;
        lastCustomLevels = state.customLevels;

        if (needsRebuild) {
          destroyDiagrams();

          if (state.mode === 'venn') {
            mainCard.setTitleKey('card.venn');
            buildVenn(state);
          } else {
            mainCard.setTitleKey('card.tree');
            buildTreeDiagram(state);
          }
        }

        if (state.mode === 'tree' && state.treePresetId === 'dice-roll' && diceCrossedSumBoundary) {
          updateConditionOptions(state);
        }
      }

      return {
        update,
        dispose() {
          destroyDiagrams();
          dataTable.destroy();
          tableCard.destroy();
          mainCard.destroy();
          probsCard.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

// ---------------------------------------------------------------------------
// Public exports
// ---------------------------------------------------------------------------

export const experiment = defineExperiment(buildSpec());

/** Backwards-compatible alias — keeps index.ts + test green without edits. */
export const mountProbabilityDiagrams = experiment.mount.bind(experiment);
